import React, { useEffect, useMemo, useState } from 'react';
import './assets/styles/CompanyKPIContainer.scss';
import './assets/styles/CampaignAndChannelContainer.scss';
import IconArrowGround from './assets/images/icons/arrow-ground.svg';
import { downloadTableAsExcel, getDateFromString, isPartialMatch } from '../../utils/helpers';
import { addMonths, format, isAfter, lastDayOfMonth, startOfMonth, sub } from 'date-fns';
import { filterType } from '../../constants/filterOptions';
import { Badge } from 'react-bootstrap';
import { DateFilter } from './components';
import { useDispatch, useSelector } from 'react-redux';
import { getCampaignAndChannel } from './store/actions';
import TableComponent from '../commons/components/TableComponent';
import { Placeholder } from '../commons/components';
import { useNavigate } from 'react-router';

const CampaignAndChannelContainer = () => {
  const DEFAULT_RANGE_MONTHS = 3;
  const now = new Date();

  // startDateDate should be start of year
  const startDateDate = startOfMonth(addMonths(new Date(), -DEFAULT_RANGE_MONTHS));
  const endDateDate = sub(now, { days: 1 });
  const [startValue, setStartValue] = useState(startDateDate);
  const [endValue, setEndValue] = useState(endDateDate);
  const campaignAndChannel = useSelector((state) => state.kpi.campaignAndChannel);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [currentView, setCurrentView] = useState(filterType.custom);
  const [searchText, setSearchText] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (startValue && endValue) {
      dispatch(getCampaignAndChannel({
        startDate: format(startValue, 'yyyy-MM-dd'),
        endDate: format(endValue, 'yyyy-MM-dd'),
      }));
    }
  }, [startValue, endValue]);

  function getMonthsInRange(startDate, endDate) {
    const months = [];
    let currentDate = new Date(startDate);
    let currentEndDate = new Date(endDate);
    while (
      isAfter(currentEndDate, currentDate) ||
      currentDate.getMonth() === currentEndDate.getMonth()
    ) {
      months.push(format(lastDayOfMonth(currentDate), 'yyyy-MM-dd'));
      currentDate = addMonths(currentDate, 1);
    }
    return months;
  }

  const monthsInRange = getMonthsInRange(startValue, endValue);

  const columns = React.useMemo(() => {
    monthsInRange.forEach((month, index) => {
      monthsInRange[index] = getDateFromString(month).toLocaleString('default', {
        month: 'long',
      });
    });
    const monthColumns = monthsInRange.map((monthName) => ({
      Header: monthName,
      accessor: monthName,
      Cell: ({ cell }) => cell.value,
    }));

    const baseColumns = [
      {
        Header: 'Campaign',
        accessor: 'campaign',
        enableRowSpan: true,
        clickable: true,
        Cell: ({ cell }) => cell.value,
      },
      {
        Header: 'Channel',
        accessor: 'channel',
        Cell: ({ cell }) => cell.value,
      },
    ];
    return [...baseColumns, ...monthColumns];
  }, [campaignAndChannel]);

  const data = useMemo(() => {
    if (!campaignAndChannel.data || campaignAndChannel.data.length === 0) {
      const placeholderRows = Array.from({ length: 20 }, (_, index) => ({
        campaign: '-',
        channel: '-',
        ...monthsInRange.reduce((acc, month) => {
          acc[month] = '-';
          return acc;
        }, {}),
      }));
      return placeholderRows;
    }
    const newArray = [];
    
    const filteredCampaignAndChannel = (campaignAndChannel.data || []).filter(campaign => {
      return campaign.campaign.toLowerCase().includes(searchText.toLowerCase());
    });

    filteredCampaignAndChannel.map(campaign => {
      campaign.channels.forEach(channel => {
        const data = {
          campaign: campaign.campaign,
          channel: channel.channel,
        }
        channel.months.forEach(month => {
          const monthName = getDateFromString(month.active_date).toLocaleString('default', {
            month: 'long',
          });
          data[monthName] = month.campaign_users;
        });
        newArray.push(data);
      });
    });
    return newArray;
  }, [campaignAndChannel, searchText]);

  async function downloadSheet() {
    const tableElement = document.getElementById('data-table-container');
    const rangeText = `${format(getDateFromString(startValue), 'yyyy-MM-dd')} to ${format(
      getDateFromString(endValue),
      'yyyy-MM-dd'
    )}`;
    await downloadTableAsExcel(tableElement, `campaign-channel-(${rangeText}).xlsx`);
  }

  const onClickDateFilter = (e) => {
    e.stopPropagation();
    setShowDateFilter(!showDateFilter);
  };

  const onFilterUpdate = (selectedFilter, sDate, eDate) => {
    setStartValue(sDate);
    setEndValue(eDate);
    setCurrentView(selectedFilter.value);
  };

  const onClick = (cell) => {
    if (cell.column.id === 'campaign') {
      navigate(`/campaign-details/${cell.value}`, {
        state: {
          startDate: format(startValue, 'yyyy-MM-dd'),
          endDate: format(endValue, 'yyyy-MM-dd'),
        }
      });
    }
  }

  return (
    <div className='container-fluid kpi campaign-and-channel-container'>
      <div className='company-report--actions'>
        <div className='search-campaign'>
          <input type='text' placeholder='Search campaign...' onChange={e => setSearchText(e.target.value)} />
        </div>
        <div className='d-flex align-items-center gap-3'>
          <div className='company-report--actions--date'>
            <div className='company-report--actions--date--wrapper' onClick={onClickDateFilter}>
              <Badge className='current-filter' bg='light' text='dark'>
                {currentView || 'Custom'}
              </Badge>
              {startValue && endValue && (
                <span className='date-range'>
                  {format(getDateFromString(startValue), 'MMM d')} -{' '}
                  {format(getDateFromString(endValue), 'MMM d, yyyy')}
                </span>
              )}
              <span className='select-icon-down' aria-hidden='true'></span>
            </div>
            {showDateFilter && (
              <DateFilter
                onClose={() => setShowDateFilter(false)}
                startValue={startValue}
                endValue={endValue}
                onFilterUpdate={onFilterUpdate}
              />
            )}
          </div>
          <div className={`company-report--actions--download`} onClick={() => downloadSheet()}>
            <img src={IconArrowGround} alt='' />
            <span>Download</span>
          </div>
        </div>
      </div>
      <div id='data-table-container' className='data-table-container table-responsive mt-3'>
        <TableComponent columns={columns} data={data} onClick={onClick} />
      </div>
    </div>
  );
};

export default CampaignAndChannelContainer;
